import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Center,
  Button,
  Input,
  Spinner,
  Pressable,
  useToast,
  Select,
  CheckIcon,
  Image,
} from "native-base";
import { StyleSheet, View, PanResponder, Platform } from "react-native";
import { GlobalStyles } from "../../constants/styles";
import { useSelector } from "react-redux";
import axios from "axios";
import Toast from "../../components/Toast";
import { GlobalConstants } from "../../constants/constants";

// const buildApiUrl = (baseUrl, port) => {
//   const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
//   return `${cleanBaseUrl}:${port}`;
// };

// const LOYALTY_API_BASE_URL = buildApiUrl(GlobalConstants.servicesBaseUrl, "3005");

const LOYALTY_API_BASE_URL = `${GlobalConstants.LoyaltyBaseUrl}`.replace(/\/$/, "");


const SLIDER_MIN = 0;
const SLIDER_MAX = 10000;
const SLIDER_MIN_DISTANCE = 10;

const styles = StyleSheet.create({
  pageBg: { backgroundColor: GlobalStyles.colors.lightGrey },
});

const gradientStyle = {
  background: "linear-gradient(90deg, #000561 0%, #000AC7 100%)",
  backgroundColor: GlobalStyles.colors.gradientBlueStart,
};

const titleCase = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());

const safeNum = (v, fallback = 0) => {
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : fallback;
};
const safeInt = (v, fallback = 0) => {
  const n = typeof v === "number" ? v : parseInt(String(v), 10);
  return Number.isFinite(n) ? n : fallback;
};
const ensureBoolOrDefault = (v, fallback) =>
  typeof v === "boolean" ? v : fallback;
const ensureObjectOrDefault = (v, fallback = {}) =>
  v && typeof v === "object" && !Array.isArray(v) ? v : fallback;
const unwrap = (res) =>
  res && typeof res === "object" && "status" in res
    ? res
    : { status: true, data: res };
const is404 = (err) => err?.response?.status === 404;
const formatPointsLike = (val, fallback = "0.00") => {
  const n = safeNum(String(val ?? "").trim(), NaN);
  return Number.isFinite(n) ? n.toFixed(2) : fallback;
};


const makeApiCall = async (method, endpoint, data = null, token, retries = 2) => {
  const url = `${LOYALTY_API_BASE_URL}${endpoint}`;
  const config = {
    method,
    url,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    timeout: 30000,
  };
  if (data && (method === "POST" || method === "PUT" || method === "PATCH"))
    config.data = data;
  else if (data && method === "GET") config.params = data;
  try {
    const response = await axios(config);
    return response.data;
  } catch (error) {
    if (
      retries > 0 &&
      (error.code === "ECONNABORTED" || error.message.includes("timeout"))
    ) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return makeApiCall(method, endpoint, data, token, retries - 1);
    }
    throw error;
  }
};

const configurationApi = {
  getUserConfig: (token) =>
    makeApiCall("GET", "/api/configuration/user", null, token),
  updateUserConfig: (token, configData) =>
    makeApiCall("PUT", "/api/configuration/user", configData, token),
  getTierConfig: (token) =>
    makeApiCall("GET", "/api/configuration/tier-config", null, token),
  saveTierConfig: (token, tiers) =>
    makeApiCall("POST", "/api/configuration/tier-config", { tiers }, token),
  getUserLoyaltyPoints: (token) =>
    makeApiCall("GET", "/api/configuration/user-loyalty-points", null, token),
  updateImportExportSettings: (token, allowImport, allowExport) =>
    makeApiCall(
      "PUT",
      "/api/configuration/import-export-settings",
      {
        allow_import_to_tt: Boolean(allowImport),
        allow_export_from_tt: Boolean(allowExport),
      },
      token,
    ),
  importToTT: (token, pointsAmount, sourcePool = null, description = null) => {
    const body = { points_amount: safeNum(pointsAmount, NaN) };
    if (sourcePool) body.source_pool = String(sourcePool);
    if (description) body.description = String(description);
    return makeApiCall("POST", "/api/configuration/import-to-tt", body, token);
  },
  exportFromTT: (
    token,
    pointsAmount,
    destinationPool = null,
    description = null,
  ) => {
    const body = { points_amount: safeNum(pointsAmount, NaN) };
    if (destinationPool) body.destination_pool = String(destinationPool);
    if (description) body.description = String(description);
    return makeApiCall(
      "POST",
      "/api/configuration/export-from-tt",
      body,
      token,
    );
  },
};

const dashboardApi = {
  getUserDashboard: (token, period = "all") =>
    makeApiCall("GET", "/api/dashboard/user", { period }, token),
};

const pointsApi = {
  issue: (token, payload) =>
    makeApiCall("POST", "/api/points/issue", payload, token),

  redeem: (token, pointsToRedeem, qrCodeData = null) => {
    const body = { points_to_redeem: safeNum(pointsToRedeem, NaN) };
    if (qrCodeData) body.qr_code_data = String(qrCodeData);
    return makeApiCall("POST", "/api/points/redeem", body, token);
  },


  generateQR: (token, pointsAmount) =>
    makeApiCall(
      "POST",
      "/api/points/generate-qr",
      { points_amount: safeNum(pointsAmount, NaN) },
      token,
    ),

  getUserPointsSummary: (token, userId) =>
    makeApiCall("GET", `/api/points/user/points/${userId}`, null, token),
};

const YesNoGradientToggle = ({ value, onChange, isDisabled }) => (
  <HStack space={0} opacity={isDisabled ? 0.6 : 1}>
    <Pressable onPress={() => !isDisabled && onChange(true)}>
      <Box
        w={20}
        h={8}
        alignItems="center"
        justifyContent="center"
        borderTopLeftRadius={2}
        borderBottomLeftRadius={2}
        borderWidth={1}
        borderColor={GlobalStyles.colors.blue}
        style={
          value === true
            ? gradientStyle
            : { backgroundColor: GlobalStyles.colors.white }
        }
      >
        <Text
          style={{
            color:
              value === true
                ? GlobalStyles.colors.white
                : GlobalStyles.colors.blue,
            fontWeight: "bold",
            fontSize: 14,
            fontFamily: GlobalStyles.text.fontFamily,
          }}
        >
          Yes
        </Text>
      </Box>
    </Pressable>
    <Pressable onPress={() => !isDisabled && onChange(false)}>
      <Box
        w={20}
        h={8}
        alignItems="center"
        justifyContent="center"
        borderTopRightRadius={2}
        borderBottomRightRadius={2}
        borderWidth={1}
        borderColor={GlobalStyles.colors.blue}
        style={
          value === false
            ? gradientStyle
            : { backgroundColor: GlobalStyles.colors.white }
        }
      >
        <Text
          style={{
            color:
              value === false
                ? GlobalStyles.colors.white
                : GlobalStyles.colors.blue,
            fontWeight: "bold",
            fontSize: 14,
            fontFamily: GlobalStyles.text.fontFamily,
          }}
        >
          No
        </Text>
      </Box>
    </Pressable>
  </HStack>
);

const TwoThumbRangeSlider = ({
  value,
  onChange,
  min = SLIDER_MIN,
  max = SLIDER_MAX,
  step = 1,
  minDistance = SLIDER_MIN_DISTANCE,
  disabled = false,
}) => {
  const trackWidthRef = useRef(1);
  const trackPageXRef = useRef(0);
  const activeThumbRef = useRef(null);
  const minRef = useRef(value?.[0] ?? min);
  const maxRef = useRef(value?.[1] ?? max);

  useEffect(() => {
    minRef.current = value?.[0] ?? min;
    maxRef.current = value?.[1] ?? max;
  }, [value, min, max]);

  const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
  const snap = (n) => (step > 0 ? Math.round(n / step) * step : n);
  const valueToPx = (v) => {
    const t = (v - min) / Math.max(1, max - min);
    return clamp(t, 0, 1) * trackWidthRef.current;
  };
  const pxToValue = (px) => {
    const t = clamp(px / Math.max(1, trackWidthRef.current), 0, 1);
    return min + t * (max - min);
  };
  const pickThumb = (xPx) => {
    const minPx = valueToPx(minRef.current);
    const maxPx = valueToPx(maxRef.current);
    return Math.abs(xPx - minPx) <= Math.abs(xPx - maxPx) ? "min" : "max";
  };
  const updateFromPageX = (pageX) => {
    const xPx = pageX - trackPageXRef.current;
    const v = clamp(snap(pxToValue(xPx)), min, max);
    const active = activeThumbRef.current;
    if (!active) return;
    const minValue = minRef.current;
    const maxValue = maxRef.current;
    if (active === "min") {
      const clamped = clamp(
        Math.min(v, maxValue - minDistance),
        min,
        maxValue - minDistance,
      );
      onChange([clamped, maxValue]);
    } else {
      const clamped = clamp(
        Math.max(v, minValue + minDistance),
        minValue + minDistance,
        max,
      );
      onChange([minValue, clamped]);
    }
  };

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => !disabled,
        onMoveShouldSetPanResponder: () => !disabled,
        onPanResponderGrant: (evt) => {
          if (disabled) return;
          const xPx = evt.nativeEvent.pageX - trackPageXRef.current;
          activeThumbRef.current = pickThumb(xPx);
          updateFromPageX(evt.nativeEvent.pageX);
        },
        onPanResponderMove: (evt) => {
          if (disabled) return;
          updateFromPageX(evt.nativeEvent.pageX);
        },
        onPanResponderTerminationRequest: () => false,
        onShouldBlockNativeResponder: () => true,
        onPanResponderRelease: () => {
          activeThumbRef.current = null;
        },
        onPanResponderTerminate: () => {
          activeThumbRef.current = null;
        },
      }),
    [disabled, min, max, step, minDistance],
  );

  const [minValue, maxValue] = Array.isArray(value) ? value : [min, max];
  const span = Math.max(1, max - min);
  const toPct = (v) => ((v - min) / span) * 100;
  const minPct = toPct(minValue);
  const maxPct = toPct(maxValue);

  return (
    <View style={{ width: "100%" }}>
      <View
        {...panResponder.panHandlers}
        style={{ height: 92, justifyContent: "center" }}
      >
        <View
          ref={(ref) => {
            if (!ref) return;
            requestAnimationFrame(() => {
              try {
                ref.measure((x, y, w, h, pageX) => {
                  trackWidthRef.current = Math.max(1, w);
                  trackPageXRef.current = pageX;
                });
              } catch { }
            });
          }}
          onLayout={(e) => {
            trackWidthRef.current = Math.max(1, e.nativeEvent.layout.width);
          }}
          style={{
            height: 14,
            borderRadius: 7,
            backgroundColor: GlobalStyles.colors.grey,
            marginTop: 26,
          }}
        >
          <View
            style={{
              position: "absolute",
              left: `${minPct}%`,
              width: `${Math.max(0, maxPct - minPct)}%`,
              top: 0,
              bottom: 0,
              borderRadius: 7,
              background: "linear-gradient(90deg, #0F135C 0%, #000AC7 100%)",
              backgroundColor: GlobalStyles.colors.blue,
            }}
          />
        </View>
        <View
          pointerEvents="none"
          style={{
            position: "absolute",
            top: 19,
            left: `${minPct}%`,
            transform: [{ translateX: -14 }],
            width: 28,
            height: 28,
            borderRadius: 14,
            backgroundColor: GlobalStyles.colors.white,
            borderWidth: 2,
            borderColor: GlobalStyles.colors.blue,
          }}
        />
        <View
          pointerEvents="none"
          style={{
            position: "absolute",
            top: 19,
            left: `${maxPct}%`,
            transform: [{ translateX: -14 }],
            width: 28,
            height: 28,
            borderRadius: 14,
            backgroundColor: GlobalStyles.colors.white,
            borderWidth: 2,
            borderColor: GlobalStyles.colors.blue,
          }}
        />
        <View
          pointerEvents="none"
          style={{
            position: "absolute",
            top: 56,
            left: `${minPct}%`,
            transform: [{ translateX: -30 }],
          }}
        >
          <View
            style={{
              backgroundColor: "#fff",
              paddingHorizontal: 16,
              paddingVertical: 6,
              borderRadius: 4,
              borderWidth: 1,
              borderColor: "#E0E0E0",
            }}
          >
            <Text
              style={{
                fontFamily: GlobalStyles.text.fontFamily,
                fontSize: 16,
                color: GlobalStyles.colors.black,
              }}
            >
              {minValue}
            </Text>
          </View>
        </View>
        <View
          pointerEvents="none"
          style={{
            position: "absolute",
            top: 56,
            left: `${maxPct}%`,
            transform: [{ translateX: -30 }],
          }}
        >
          <View
            style={{
              backgroundColor: "#fff",
              paddingHorizontal: 16,
              paddingVertical: 6,
              borderRadius: 4,
              borderWidth: 1,
              borderColor: "#E0E0E0",
            }}
          >
            <Text
              style={{
                fontFamily: GlobalStyles.text.fontFamily,
                fontSize: 16,
                color: GlobalStyles.colors.black,
              }}
            >
              {maxValue}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const LoyaltySettingsStep1 = ({
  locked,
  allowImportExport,
  setAllowImportExport,
  haveTiers,
  setHaveTiers,
  pointsValue,
  setPointsValue,
  dollarsValue,
  setDollarsValue,
  totalPointsIssued,
  onNext,
  onSave,
  isSaving,
}) => (
  <Center flex={1} style={styles.pageBg} px={4}>
    <Box
      width="100%"
      maxW={443}
      marginTop={30}
      borderRadius={12}
      backgroundColor={GlobalStyles.colors.white}
      alignSelf="center"
      shadow={2}
      p={6}
    >
      <VStack space={6} alignItems="center" w="100%">
        <Box
          w="100%"
          maxW={309}
          borderRadius={0}
          borderWidth={0.2}
          borderColor={GlobalStyles.colors.black}
          backgroundColor={GlobalStyles.colors.white}
          p={6}
          alignItems="flex-start"
          justifyContent="center"
        >
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 16,
              color: GlobalStyles.colors.black,
              fontWeight: 500,
              marginBottom: 18,
            }}
          >
            Allow import export to TT points?
          </Text>
          <YesNoGradientToggle
            value={allowImportExport}
            onChange={setAllowImportExport}
            isDisabled={locked}
          />
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 16,
              color: GlobalStyles.colors.black,
              fontWeight: 500,
              marginTop: 20,
              marginBottom: 8,
            }}
          >
            Have Tiers?
          </Text>
          <YesNoGradientToggle
            value={haveTiers}
            onChange={setHaveTiers}
            isDisabled={locked}
          />
        </Box>
        <Box
          w="100%"
          maxW={309}
          borderRadius={0}
          borderWidth={0.2}
          borderColor={GlobalStyles.colors.black}
          backgroundColor={GlobalStyles.colors.white}
          p={6}
          alignItems="flex-start"
          justifyContent="center"
        >
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 16,
              color: GlobalStyles.colors.black,
              fontWeight: 500,
              marginBottom: 14,
            }}
          >
            Points Earning Rate
          </Text>
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 14,
              color: GlobalStyles.colors.black,
              fontWeight: 500,
              marginBottom: 12,
            }}
          >
            Enter how many points for how many dollars
          </Text>

          <HStack alignItems="flex-end" space={4} mb={4}>
            <VStack alignItems="center" space={1}>
              <View
                style={{
                  width: 80,
                  height: 44,
                  borderWidth: 1,
                  borderColor: GlobalStyles.colors.blue,
                  justifyContent: "center",
                  alignItems: "center",
                  background: "linear-gradient(90deg, #000561 0%, #000AC7 100%)",
                  backgroundColor: GlobalStyles.colors.blue,
                }}
              >
                <Input
                  value={pointsValue}
                  onChangeText={locked ? undefined : setPointsValue}
                  isReadOnly={locked}
                  variant="unstyled"
                  textAlign="center"
                  color={GlobalStyles.colors.white}
                  fontWeight="bold"
                  fontSize={20}
                  keyboardType="numeric"
                  p={0}
                  style={{
                    fontFamily: GlobalStyles.text.fontFamily,
                    width: 78,
                    height: 42,
                    textAlign: "center",
                    color: GlobalStyles.colors.white,
                  }}
                />
              </View>
              <Text
                style={{
                  fontFamily: GlobalStyles.text.fontFamily,
                  fontSize: 13,
                  color: GlobalStyles.colors.black,
                  marginTop: 4,
                }}
              >
                Point
              </Text>
            </VStack>

            <Text
              style={{
                fontFamily: GlobalStyles.text.fontFamily,
                fontSize: 16,
                color: GlobalStyles.colors.black,
                fontWeight: "500",
                marginBottom: 22,
              }}
            >
              for
            </Text>

            <VStack alignItems="center" space={1}>
              <View
                style={{
                  width: 80,
                  height: 44,
                  borderWidth: 1,
                  borderColor: GlobalStyles.colors.blue,
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: GlobalStyles.colors.white,
                }}
              >
                <Input
                  value={dollarsValue}
                  onChangeText={locked ? undefined : setDollarsValue}
                  isReadOnly={locked}
                  variant="unstyled"
                  textAlign="center"
                  color={GlobalStyles.colors.blue}
                  fontWeight="bold"
                  fontSize={20}
                  keyboardType="numeric"
                  p={0}
                  style={{
                    fontFamily: GlobalStyles.text.fontFamily,
                    width: 78,
                    height: 42,
                    textAlign: "center",
                    color: GlobalStyles.colors.blue,
                  }}
                />
              </View>
              <Text
                style={{
                  fontFamily: GlobalStyles.text.fontFamily,
                  fontSize: 13,
                  color: GlobalStyles.colors.black,
                  marginTop: 4,
                }}
              >
                Dollar
              </Text>
            </VStack>
          </HStack>

          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 16,
              color: GlobalStyles.colors.black,
              fontWeight: "500",
              marginBottom: 8,
            }}
          >
            Total Points
          </Text>
          <View
            style={{
              width: 120,
              height: 44,
              borderWidth: 1,
              borderColor: GlobalStyles.colors.blue,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: GlobalStyles.colors.white,
            }}
          >
            <Text
              style={{
                fontFamily: GlobalStyles.text.fontFamily,
                fontSize: 18,
                color: GlobalStyles.colors.black,
                fontWeight: "bold",
              }}
            >
              {totalPointsIssued ?? "—"}
            </Text>
          </View>
        </Box>

        {haveTiers === false ? (
          <Button
            mt={2}
            alignSelf="center"
            w={48}
            h={12}
            borderRadius={6}
            bg={GlobalStyles.colors.blue}
            _text={{
              color: GlobalStyles.colors.white,
              fontFamily: GlobalStyles.text.fontFamily,
              fontWeight: "bold",
              fontSize: 16,
            }}
            onPress={onSave}
            isLoading={isSaving}
            isLoadingText="Saving..."
            isDisabled={
              allowImportExport === null ||
              haveTiers === null ||
              !pointsValue ||
              !dollarsValue
            }
          >
            Save Configuration
          </Button>
        ) : (
          <Button
            mt={2}
            alignSelf="center"
            w={40}
            h={12}
            borderRadius={6}
            bg={GlobalStyles.colors.blue}
            _text={{
              color: GlobalStyles.colors.white,
              fontFamily: GlobalStyles.text.fontFamily,
              fontWeight: "bold",
              fontSize: 16,
            }}
            onPress={onNext}
            isDisabled={
              allowImportExport === null ||
              haveTiers === null ||
              !pointsValue ||
              !dollarsValue
            }
          >
            Next
          </Button>
        )}
      </VStack>
    </Box>
  </Center>
);

const TiersSetupCard = ({
  locked,
  numTiersStr,
  setNumTiersStr,
  tierRange,
  setTierRange,
  onNext,
  onBack,
}) => {
  const numTiers = Math.max(2, Math.min(10, safeInt(numTiersStr, 3)));
  return (
    <Center flex={1} style={styles.pageBg} px={4}>
      <Box
        w="100%"
        maxW={492}
        borderWidth={1}
        borderColor={GlobalStyles.colors.black}
        backgroundColor="#D9D9D940"
        mt={8}
        alignSelf="center"
        overflow="hidden"
      >
        <HStack
          bg="#D9D9D9"
          borderBottomWidth={0.2}
          borderBottomColor={GlobalStyles.colors.black}
          h="52px"
          alignItems="center"
          justifyContent="space-between"
          px={6}
        >
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 18,
              color: GlobalStyles.colors.black,
              fontWeight: 500,
            }}
          >
            How many Tiers?
          </Text>
          <View
            style={{
              width: 64,
              height: 40,
              borderWidth: 2,
              borderColor: GlobalStyles.colors.blue,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: GlobalStyles.colors.white,
              borderRadius: 2,
            }}
          >
            <Input
              value={numTiersStr}
              onChangeText={(t) => {
                if (locked) return;
                const clean = t.replace(/[^0-9]/g, "").slice(0, 2);
                setNumTiersStr(clean);
              }}
              isReadOnly={locked}
              variant="unstyled"
              textAlign="center"
              color={GlobalStyles.colors.blue}
              fontWeight="bold"
              fontSize={20}
              keyboardType="numeric"
              maxLength={2}
              p={0}
              style={{
                fontFamily: GlobalStyles.text.fontFamily,
                width: 60,
                height: 38,
                textAlign: "center",
                color: GlobalStyles.colors.blue,
              }}
            />
          </View>
        </HStack>

        <VStack px={6} py={6} space={4}>
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 16,
              color: GlobalStyles.colors.black,
              fontWeight: 500,
            }}
          >
            Select Tiers Points
          </Text>
          <TwoThumbRangeSlider
            value={tierRange}
            onChange={locked ? () => { } : setTierRange}
            min={SLIDER_MIN}
            max={SLIDER_MAX}
            step={1}
            minDistance={SLIDER_MIN_DISTANCE}
            disabled={locked}
          />
          <Text
            style={{
              fontFamily: GlobalStyles.text.fontFamily,
              fontSize: 13,
              color: "#666",
              textAlign: "center",
              marginTop: 4,
            }}
          >
            {numTiers} Tiers Example
          </Text>
          <HStack justifyContent="space-between" mt={2}>
            <Button
              variant="outline"
              borderColor={GlobalStyles.colors.blue}
              _text={{ color: GlobalStyles.colors.blue, fontWeight: "bold", fontSize: 15 }}
              onPress={onBack}
              w={100}
              h={12}
              borderRadius={6}
            >
              Back
            </Button>
            <Button
              bg={GlobalStyles.colors.blue}
              _text={{ color: GlobalStyles.colors.white, fontWeight: "bold", fontSize: 15 }}
              onPress={onNext}
              w={100}
              h={12}
              borderRadius={6}
            >
              Next
            </Button>
          </HStack>
        </VStack>
      </Box>
    </Center>
  );
};

const NameTiersCard = ({
  locked,
  numTiers,
  tierNames,
  setTierNames,
  onBack,
  onSave,
  isSaving,
}) => {
  const defaultPlaceholders = ["Silver", "Gold", "Diamond", "Platinum", "Elite", "Sapphire"];

  const handleChange = (i, val) => {
    if (locked) return;
    const next = [...tierNames];
    next[i] = val;
    setTierNames(next);
  };

  return (
    <Center flex={1} style={styles.pageBg} px={4}>
      <Box
        w="100%"
        maxW={600}
        bg={GlobalStyles.colors.white}
        borderRadius={12}
        mt={8}
        alignSelf="center"
        shadow={2}
        px={8}
        py={6}
      >
        <Text
          style={{
            fontFamily: GlobalStyles.text.fontFamily,
            fontSize: 18,
            color: GlobalStyles.colors.black,
            fontWeight: "500",
            marginBottom: 12,
          }}
        >
          Name Tiers
        </Text>
        <HStack
          justifyContent="center"
          alignItems="flex-start"
          space={0}
          py={2}
        >
          <VStack>
            {Array.from({ length: numTiers }).map((_, i) => (
              <Box
                key={`num-${i}`}
                w="120px"
                h="36px"
                borderWidth={1}
                borderColor="#2E2E2E"
                bg={GlobalStyles.colors.white}
                alignItems="center"
                justifyContent="center"
                mt={i === 0 ? 0 : -1}
              >
                <Text
                  style={{
                    fontFamily: GlobalStyles.text.fontFamily,
                    fontSize: 16,
                    color: GlobalStyles.colors.black,
                  }}
                >
                  {i + 1}
                </Text>
              </Box>
            ))}
          </VStack>
          <VStack>
            {Array.from({ length: numTiers }).map((_, i) => (
              <Box
                key={`name-${i}`}
                w="120px"
                h="36px"
                borderWidth={1}
                borderColor="#2E2E2E"
                bg={GlobalStyles.colors.white}
                mt={i === 0 ? 0 : -1}
                px={3}
                justifyContent="center"
              >
                <Input
                  value={tierNames[i] ?? ""}
                  onChangeText={(val) => handleChange(i, val)}
                  isReadOnly={locked}
                  variant="unstyled"
                  placeholder={defaultPlaceholders[i] || `Tier ${i + 1}`}
                  color={GlobalStyles.colors.black}
                  fontSize={15}
                  textAlign="center"
                  style={{ fontFamily: GlobalStyles.text.fontFamily }}
                />
              </Box>
            ))}
          </VStack>
        </HStack>
        <HStack justifyContent="space-between" w="100%" mt={6}>
          <Button
            variant="outline"
            borderColor={GlobalStyles.colors.blue}
            _text={{ color: GlobalStyles.colors.blue, fontWeight: "bold", fontSize: 15 }}
            onPress={onBack}
            w={100}
            h={12}
            borderRadius={6}
          >
            Back
          </Button>
          <Button
            bg={GlobalStyles.colors.blue}
            _text={{
              color: GlobalStyles.colors.white,
              fontFamily: GlobalStyles.text.fontFamily,
              fontWeight: "bold",
              fontSize: 16,
            }}
            onPress={onSave}
            isLoading={isSaving}
            isLoadingText="Saving..."
            w={100}
            h={12}
            borderRadius={6}
          >
            Save
          </Button>
        </HStack>
      </Box>
    </Center>
  );
};

const LoyaltySettings = ({ toast, user }) => {
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [allowImportExport, setAllowImportExport] = useState(true);
  const [haveTiers, setHaveTiers] = useState(null);
  const [pointsValue, setPointsValue] = useState("2");
  const [dollarsValue, setDollarsValue] = useState("1");
  const [totalPointsIssued, setTotalPointsIssued] = useState(null);

  const [numTiersStr, setNumTiersStr] = useState("3");
  const [tierRange, setTierRange] = useState([0, 5000]);
  const [tierNames, setTierNames] = useState([
    "Silver", "Gold", "Diamond", "Platinum", "Elite", "Sapphire",
  ]);

  const locked = false;

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setIsLoading(true);
      const [cfgRaw, tierRaw, loyaltyRaw] = await Promise.all([
        configurationApi.getUserConfig(user.token),
        configurationApi.getTierConfig(user.token).catch(() => null),
        configurationApi.getUserLoyaltyPoints(user.token).catch(() => null),
      ]);

      const cfgRes = unwrap(cfgRaw);
      if (cfgRes.status && cfgRes.data) {
        const c = cfgRes.data;
        const imp = c.allow_import_to_tt ?? c.loyalty_allow_import_to_tt;
        const exp = c.allow_export_from_tt ?? c.loyalty_allow_export_from_tt;
        setAllowImportExport(
          ensureBoolOrDefault(imp, ensureBoolOrDefault(exp, true)),
        );
        setHaveTiers(ensureBoolOrDefault(c.tiers_enabled, null));
        if (c.points_per_dollar_points != null)
          setPointsValue(String(c.points_per_dollar_points));
        if (c.points_per_dollar_dollars != null)
          setDollarsValue(String(c.points_per_dollar_dollars));
        if (c.number_of_tiers && c.number_of_tiers > 0)
          setNumTiersStr(String(c.number_of_tiers));
      }

      if (tierRaw) {
        const tierRes = unwrap(tierRaw);
        if (tierRes.status && tierRes.data) {
          const tiers = tierRes.data.tiers || [];
          if (tiers.length > 0) {
            setNumTiersStr(String(tiers.length));
            setTierNames(tiers.map((t) => t.tier_name || ""));
            const minP = Math.min(...tiers.map((t) => safeNum(t.min_points, 0)));
            const maxP = Math.max(
              ...tiers.map((t) => safeNum(t.max_points, 5000)),
            );
            setTierRange([
              Math.max(SLIDER_MIN, Math.min(minP, SLIDER_MAX)),
              Math.max(SLIDER_MIN, Math.min(maxP, SLIDER_MAX)),
            ]);
          }
        }
      }

      if (loyaltyRaw) {
        const loyRes = unwrap(loyaltyRaw);
        if (loyRes.status && loyRes.data?.loyaltyPoints?.points_issued != null) {
          setTotalPointsIssued(
            String(Math.round(loyRes.data.loyaltyPoints.points_issued)),
          );
        }
      }

      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message={"Configuration loaded successfully!"}
          />
        ),
        placement: "top",
        duration: 2500,
      });
    } catch (error) {
      console.error("Error loading configuration:", error);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Failed to load configuration. Using defaults."}
          />
        ),
        placement: "top",
        duration: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveConfiguration = async () => {
    try {
      setIsSaving(true);
      const numTiers = Math.max(2, Math.min(10, safeInt(numTiersStr, 3)));

      const configData = {
        user_type: "business",
        loyalty_type: "points",
        calculation_type: "fixed",
        allow_import_to_tt: Boolean(allowImportExport),
        allow_export_from_tt: Boolean(allowImportExport),
        points_per_dollar_points: safeNum(pointsValue, 2),
        points_per_dollar_dollars: safeNum(dollarsValue, 1),
        tiers_enabled: Boolean(haveTiers),
        number_of_tiers: haveTiers ? numTiers : 0,
        is_active: true,
      };

      const res = unwrap(
        await configurationApi.updateUserConfig(user.token, configData),
      );
      if (!res.status) throw new Error(res.status_msg || "Save failed");

      if (haveTiers) {
        const totalSpan = safeNum(tierRange[1], 5000) - safeNum(tierRange[0], 0);
        const segSize = totalSpan / numTiers;
        const tiersPayload = Array.from({ length: numTiers }, (_, i) => ({
          tier_name: (tierNames[i] || `Tier ${i + 1}`).trim(),
          min_points: Math.round(safeNum(tierRange[0], 0) + i * segSize),
          max_points: Math.round(
            safeNum(tierRange[0], 0) + (i + 1) * segSize,
          ),
          multiplier: parseFloat((1.0 + i * 0.25).toFixed(2)),
        }));
        await configurationApi
          .saveTierConfig(user.token, tiersPayload)
          .catch((e) => {
            if (!is404(e))
              console.warn("saveTierConfig failed:", e?.message);
          });
      }

      configurationApi
        .updateImportExportSettings(
          user.token,
          Boolean(allowImportExport),
          Boolean(allowImportExport),
        )
        .catch((e) => {
          if (!is404(e))
            console.warn("updateImportExportSettings failed:", e?.message);
        });

      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message={"Settings saved successfully!"}
          />
        ),
        placement: "top",
        duration: 3000,
      });
      setTimeout(() => loadConfiguration(), 600);
    } catch (error) {
      const msg =
        error?.response?.data?.status_msg ||
        error?.message ||
        "Failed to save settings.";
      toast.show({
        render: () => <Toast color={GlobalStyles.colors.red} message={msg} />,
        placement: "top",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Center flex={1} style={styles.pageBg}>
        <Spinner size="lg" color={GlobalStyles.colors.blue} />
        <Text mt={4}>Loading configuration...</Text>
      </Center>
    );
  }

  return (
    <Box flex={1} pb="48px">
      {step === 0 && (
        <LoyaltySettingsStep1
          locked={locked}
          allowImportExport={allowImportExport}
          setAllowImportExport={setAllowImportExport}
          haveTiers={haveTiers}
          setHaveTiers={setHaveTiers}
          pointsValue={pointsValue}
          setPointsValue={setPointsValue}
          dollarsValue={dollarsValue}
          setDollarsValue={setDollarsValue}
          totalPointsIssued={totalPointsIssued}
          onNext={() => setStep(1)}
          onSave={saveConfiguration}
          isSaving={isSaving}
        />
      )}

      {step === 1 && haveTiers && (
        <TiersSetupCard
          locked={locked}
          numTiersStr={numTiersStr}
          setNumTiersStr={setNumTiersStr}
          tierRange={tierRange}
          setTierRange={setTierRange}
          onNext={() => setStep(2)}
          onBack={() => setStep(0)}
        />
      )}

      {step === 2 && haveTiers && (
        <NameTiersCard
          locked={locked}
          numTiers={Math.max(2, Math.min(10, safeInt(numTiersStr, 3)))}
          tierNames={tierNames}
          setTierNames={setTierNames}
          onBack={() => setStep(1)}
          onSave={saveConfiguration}
          isSaving={isSaving}
        />
      )}
    </Box>
  );
};

const AnalyticsRow = ({ label, value }) => (
  <HStack
    alignItems="center"
    justifyContent="space-between"
    w="100%"
    flexWrap="wrap"
    space={2}
  >
    <Text
      numberOfLines={2}
      style={{
        fontFamily: "Roboto, Arial, sans-serif",
        fontWeight: "400",
        fontSize: 14,
        lineHeight: 20,
        letterSpacing: 0.1,
        color: GlobalStyles.colors.black,
        verticalAlign: "middle",
      }}
      flexShrink={1}
    >
      {label}
    </Text>
    <Box
      mt={{ base: 2, md: 0 }}
      w="100.3px"
      h="30px"
      borderWidth={1}
      borderColor={GlobalStyles.colors.black}
      borderRadius={5}
      backgroundColor={GlobalStyles.colors.white}
      alignItems="center"
      justifyContent="center"
      px={2}
    >
      <Text
        style={{
          fontFamily: "Roboto, Arial, sans-serif",
          fontWeight: "400",
          fontSize: 18,
          lineHeight: 20,
          letterSpacing: 0.1,
          color: GlobalStyles.colors.black,
          textAlign: "center",
        }}
      >
        {value}
      </Text>
    </Box>
  </HStack>
);

const LoyaltyAnalytics = ({ toast, user }) => {
  const [period, setPeriod] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({
    loyalty: 0,
    silver: 0,
    gold: 0,
    diamond: 0,
    purchaseByLoyal: 0,
    redeemsInPoints: 0,
    redeemsInDollars: 0,
    bonusGiven: 0,
    outsidePct: 0,
  });

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    if (!user?.token) return;
    try {
      setIsLoading(true);
      const res = unwrap(
        await dashboardApi.getUserDashboard(user.token, period),
      );
      if (res.status && res.data) {
        const d = res.data;
        setAnalyticsData({
          loyalty: safeInt(d.total_customers_on_loyalty, 0),
          silver: safeInt(d.total_customers_on_silver, 0),
          gold: safeInt(d.total_customers_on_gold, 0),
          diamond: safeInt(d.total_customers_on_diamond, 0),
          purchaseByLoyal: safeInt(
            d.total_purchase_by_loyal ??
              d.total_purchase_by_loyal_customers,
            0,
          ),
          redeemsInPoints: safeInt(
            d.total_redeems_in_points ??
              d.total_redeems_by_loyal_customers_in_points,
            0,
          ),
          redeemsInDollars: safeInt(
            d.total_redeems_in_dollars ??
              d.total_redeems_by_loyal_customers_in_dollars,
            0,
          ),
          bonusGiven: safeInt(d.bonus_points_given, 0),
          outsidePct: safeInt(d.percent_purchases_outside_loyalty, 0),
        });
      }
    } catch (error) {
      console.error("Error loading analytics:", error);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Failed to load analytics"}
          />
        ),
        placement: "top",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Center flex={1} style={styles.pageBg}>
      <Box w="100%" maxW="630px" alignSelf="center" pt="20px" px={4}>
        <HStack
          w="100%"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Text
            style={{
              fontFamily: "Roboto, Arial, sans-serif",
              fontWeight: "400",
              fontStyle: "normal",
              fontSize: 19,
              lineHeight: 20,
              letterSpacing: 0.1,
              color: GlobalStyles.colors.black,
              textAlign: "left",
              verticalAlign: "middle",
            }}
          >
            Dash-Board – Analytics
          </Text>
          <Select
            selectedValue={period}
            minWidth="120"
            accessibilityLabel="Select Period"
            placeholder="Select Period"
            _selectedItem={{
              bg: GlobalStyles.colors.blue,
              endIcon: <CheckIcon size="5" />,
            }}
            mt={1}
            onValueChange={(itemValue) => setPeriod(itemValue)}
          >
            <Select.Item label="All Time" value="all" />
            <Select.Item label="This Week" value="week" />
            <Select.Item label="This Month" value="month" />
            <Select.Item label="This Year" value="year" />
          </Select>
        </HStack>
        <Box
          w="100%"
          h="52px"
          bg="#D9D9D9"
          borderWidth={0.2}
          borderColor={GlobalStyles.colors.black}
          justifyContent="center"
        >
          <Text
            style={{
              fontFamily: "Roboto, Arial, sans-serif",
              fontWeight: "400",
              fontStyle: "normal",
              fontSize: 18,
              lineHeight: 20,
              letterSpacing: 0.1,
              color: GlobalStyles.colors.black,
              textAlign: "center",
              verticalAlign: "middle",
            }}
          >
            Select Period
          </Text>
        </Box>
        {isLoading ? (
          <Center
            w="100%"
            minH="396px"
            bg="#D9D9D940"
            borderWidth={0.2}
            borderColor={GlobalStyles.colors.black}
          >
            <Spinner size="lg" color={GlobalStyles.colors.blue} />
          </Center>
        ) : (
          <Box
            w="100%"
            minH="396px"
            bg="#D9D9D940"
            borderWidth={0.2}
            borderColor={GlobalStyles.colors.black}
            overflow="hidden"
          >
            <VStack
              w="100%"
              px="25px"
              pt="22px"
              pb="22px"
              space={0}
              style={{ rowGap: 9 }}
            >
              <AnalyticsRow label="Total Customers On Loyalty" value={analyticsData.loyalty} />
              <AnalyticsRow label="Total Customers On Silver" value={analyticsData.silver} />
              <AnalyticsRow label="Total Customers On Gold" value={analyticsData.gold} />
              <AnalyticsRow label="Total Customers On Diamond" value={analyticsData.diamond} />
              <AnalyticsRow label="Total Purchase By Loyal Customers" value={analyticsData.purchaseByLoyal} />
              <AnalyticsRow label="Total Redeems By Loyal Customers in points" value={analyticsData.redeemsInPoints} />
              <AnalyticsRow label="Total Redeems By Loyal Customers in dollars" value={analyticsData.redeemsInDollars} />
              <AnalyticsRow label="Bonus Points Given" value={analyticsData.bonusGiven} />
              <AnalyticsRow label="% Purchases outside loyalty" value={analyticsData.outsidePct} />
            </VStack>
          </Box>
        )}
      </Box>
    </Center>
  );
};

const extractAvailablePointsFromSummary = (resData) => {
  const d = resData || {};
  const candidates = [
    d.points_available,
    d.pointsAvailable,
    d.available_points,
    d.availablePoints,
    d.total_points,
    d.totalPoints,
    d.points,
    d?.summary?.points_available,
    d?.data?.points_available,
  ];
  const n = candidates
    .map((x) => safeNum(x, NaN))
    .find((x) => Number.isFinite(x));
  return Number.isFinite(n) ? n : 0;
};

const computeIssuedPoints = ({
  cashAmount,
  pointsPerDollarPoints,
  pointsPerDollarDollars,
}) => {
  const dollars = safeNum(cashAmount, NaN);
  const pts = safeNum(pointsPerDollarPoints, NaN);
  const dlrs = safeNum(pointsPerDollarDollars, NaN);
  if (
    !Number.isFinite(dollars) || dollars <= 0 ||
    !Number.isFinite(pts) || pts <= 0 ||
    !Number.isFinite(dlrs) || dlrs <= 0
  )
    return 0;
  return Math.round((dollars / dlrs) * pts * 100) / 100;
};

const RedeemPoints = ({ toast, user }) => {
  const [amount, setAmount] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [oldPts, setOldPts] = useState("0.00");
  const [newPts, setNewPts] = useState("0.00");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const [qrImageUri, setQrImageUri] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    if (!user?.token) return;
    configurationApi
      .getUserConfig(user.token)
      .then((raw) => {
        const r = unwrap(raw);
        if (r.status && r.data) setCfg(r.data);
      })
      .catch(() => {});
  }, [user?.token]);

  useEffect(() => {
    if (!user?.token) return;
    const id = safeInt(String(customerId || "").trim(), NaN);
    if (!Number.isFinite(id) || id < 1) return;
    const t = setTimeout(async () => {
      try {
        const res = unwrap(
          await pointsApi.getUserPointsSummary(user.token, id),
        );
        if (res.status && res.data) {
          const avail = extractAvailablePointsFromSummary(res.data);
          setOldPts(formatPointsLike(avail));
          setNewPts(formatPointsLike(avail));
        }
      } catch {}
    }, 350);
    return () => clearTimeout(t);
  }, [customerId, user?.token]);

  useEffect(() => {
    if (isProcessing) return;
    const id = safeInt(String(customerId || "").trim(), NaN);
    if (!Number.isFinite(id) || id < 1) return;
    const oldN = safeNum(oldPts, NaN);
    if (!Number.isFinite(oldN)) return;
    const pts = safeNum(String(amount || "").replace("$", "").trim(), NaN);
    if (Number.isFinite(pts) && pts > 0) {
      setNewPts(formatPointsLike(Math.max(0, oldN - pts)));
    } else {
      setNewPts(formatPointsLike(oldN));
    }
  }, [amount, customerId, oldPts, isProcessing]);

  const handleGenerateQR = async () => {
    const pts = safeNum(String(amount || "").replace("$", "").trim(), NaN);
    if (!Number.isFinite(pts) || pts <= 0) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Enter a valid amount to generate QR"}
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    try {
      setIsGeneratingQR(true);
      const res = unwrap(await pointsApi.generateQR(user.token, pts));
      if (res.status && res.data) {
        const raw =
          res.data.qr_code ||
          res.data.qr_image ||
          res.data.image ||
          res.data.qr ||
          res.data.data ||
          (typeof res.data === "string" ? res.data : null);

        if (raw) {
          const uri = String(raw).startsWith("data:")
            ? raw
            : `data:image/png;base64,${raw}`;
          setQrImageUri(uri);
          setQrCodeData(
            JSON.stringify({
              user_id: safeInt(String(customerId || "").trim(), 0) || undefined,
              points_amount: pts,
              timestamp: Math.floor(Date.now() / 1000),
            }),
          );
          toast.show({
            render: () => (
              <Toast color={GlobalStyles.colors.green} message={"QR code generated!"} />
            ),
            placement: "top",
            duration: 2000,
          });
        }
      }
    } catch (error) {
      const msg = error?.response?.data?.status_msg || "Failed to generate QR code";
      toast.show({
        render: () => <Toast color={GlobalStyles.colors.red} message={msg} />,
        placement: "top",
        duration: 4000,
      });
    } finally {
      setIsGeneratingQR(false);
    }
  };

  const handleRedeemPoints = async () => {
    const pts = safeNum(String(amount || "").replace("$", "").trim(), NaN);
    const customerUserId = safeInt(String(customerId || "").trim(), NaN);

    if (!Number.isFinite(customerUserId) || customerUserId < 1) {
      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={"Please enter a valid Customer ID"} />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    if (!Number.isFinite(pts) || pts <= 0) {
      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={"Please enter a valid points amount to redeem"} />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }

    try {
      setIsProcessing(true);

      try {
        const oldRes = unwrap(
          await pointsApi.getUserPointsSummary(user.token, customerUserId),
        );
        if (oldRes.status && oldRes.data)
          setOldPts(formatPointsLike(extractAvailablePointsFromSummary(oldRes.data)));
      } catch {}

      const res = unwrap(
        await pointsApi.redeem(user.token, pts, qrCodeData),
      );

      if (res.status) {
        const updatedAvail = safeNum(
          res.data?.loyalty_points?.points_available,
          NaN,
        );
        if (Number.isFinite(updatedAvail)) {
          setNewPts(formatPointsLike(updatedAvail));
        } else {
          try {
            const newRes = unwrap(
              await pointsApi.getUserPointsSummary(user.token, customerUserId),
            );
            if (newRes.status && newRes.data)
              setNewPts(formatPointsLike(extractAvailablePointsFromSummary(newRes.data)));
          } catch {}
        }

        const redeemedAmt = safeNum(res.data?.points_redeemed, pts);
        toast.show({
          render: () => (
            <Toast
              color={GlobalStyles.colors.green}
              message={`Points redeemed successfully! ${formatPointsLike(redeemedAmt)} points.`}
            />
          ),
          placement: "top",
          duration: 3000,
        });

        setAmount("");
        setCustomerId("");
        setQrImageUri(null);
        setQrCodeData(null);
      } else {
        toast.show({
          render: () => (
            <Toast
              color={GlobalStyles.colors.red}
              message={res.status_msg || "Failed to redeem points"}
            />
          ),
          placement: "top",
          duration: 4000,
        });
      }
    } catch (error) {
      const apiMsg =
        error?.response?.data?.status_msg || "Failed to redeem points";
      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={apiMsg} />
        ),
        placement: "top",
        duration: 5000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Center flex={1} style={styles.pageBg}>
      <Box w="100%" maxW="1200px" mx="auto" px={4}>
        <Box alignSelf="center" mb={4}>
          <Text
            style={{
              fontFamily: "Roboto, Arial, sans-serif",
              fontWeight: "400",
              fontSize: 18,
              lineHeight: 20,
              letterSpacing: 0.1,
              color: GlobalStyles.colors.black,
            }}
          >
            Scan QR code or Enter Customer ID
          </Text>
        </Box>
        <Box
          width="100%"
          maxW={509}
          alignSelf="center"
          bg={GlobalStyles.colors.white}
          borderRadius="12px"
          shadow={2}
          pb={6}
        >
          <VStack
            w="100%"
            alignItems="center"
            justifyContent="flex-start"
            px="28px"
          >
            <Box h="22px" />
            <Text
              style={{
                fontFamily: "Roboto, Arial, sans-serif",
                fontWeight: "400",
                fontSize: 14,
                lineHeight: 18,
                color: GlobalStyles.colors.black,
              }}
            >
              Scan QR Code For Customer ID
            </Text>

            <Box mt="14px" mb="14px">
              <Box
                w="128px"
                h="128px"
                borderWidth={0}
                alignItems="center"
                justifyContent="center"
              >
                {isGeneratingQR ? (
                  <Box
                    w="100%"
                    h="100%"
                    alignItems="center"
                    justifyContent="center"
                    bg={GlobalStyles.colors.black}
                    opacity={0.08}
                  >
                    <Spinner size="lg" color={GlobalStyles.colors.blue} />
                  </Box>
                ) : qrImageUri ? (
                  <Image
                    source={{ uri: qrImageUri }}
                    alt="Loyalty QR Code"
                    w="128px"
                    h="128px"
                    resizeMode="contain"
                  />
                ) : (
                  <Box
                    w="100%"
                    h="100%"
                    bg={GlobalStyles.colors.black}
                    opacity={1}
                  />
                )}
              </Box>
            </Box>

            <VStack w="100%" space={3}>
              {[
                {
                  label: "Customer ID:",
                  value: customerId,
                  setter: setCustomerId,
                  readOnly: false,
                  keyboard: "numeric",
                },
                {
                  label: "Enter Points to Redeem:",
                  value: amount,
                  setter: setAmount,
                  readOnly: false,
                  keyboard: "numeric",
                },
                {
                  label: "Old Points before redeem:",
                  value: oldPts,
                  setter: null,
                  readOnly: true,
                  keyboard: "numeric",
                },
                {
                  label: "New Points after redeem:",
                  value: newPts,
                  setter: null,
                  readOnly: true,
                  keyboard: "numeric",
                },
              ].map(({ label, value, setter, readOnly, keyboard }) => (
                <HStack
                  key={label}
                  w="100%"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Text
                    style={{
                      fontFamily: "Roboto, Arial, sans-serif",
                      fontWeight: "400",
                      fontSize: 14,
                      lineHeight: 18,
                      color: GlobalStyles.colors.black,
                    }}
                  >
                    {label}
                  </Text>
                  <Box
                    w="200px"
                    h="32px"
                    borderWidth={1}
                    borderColor="#CCCCCC"
                    bg={GlobalStyles.colors.white}
                    justifyContent="center"
                    px={2}
                  >
                    <Input
                      value={value}
                      onChangeText={readOnly ? undefined : setter}
                      isReadOnly={readOnly}
                      variant="unstyled"
                      fontSize={14}
                      color={GlobalStyles.colors.black}
                      keyboardType={keyboard}
                      style={{ fontFamily: "Roboto, Arial, sans-serif" }}
                    />
                  </Box>
                </HStack>
              ))}
            </VStack>

            <HStack mt={4} space={3} justifyContent="center">
              <Button
                bg={GlobalStyles.colors.white}
                borderWidth={2}
                borderColor={GlobalStyles.colors.blue}
                onPress={handleGenerateQR}
                isLoading={isGeneratingQR}
                isLoadingText="Generating..."
                _text={{
                  color: GlobalStyles.colors.blue,
                  fontWeight: "bold",
                  fontSize: 14,
                  fontFamily: "Roboto, Arial, sans-serif",
                }}
                borderRadius={6}
                px={4}
                h={10}
              >
                Generate QR
              </Button>
              <Button
                bg={GlobalStyles.colors.blue}
                onPress={handleRedeemPoints}
                isLoading={isProcessing}
                isLoadingText="Processing..."
                _text={{
                  color: GlobalStyles.colors.white,
                  fontWeight: "bold",
                  fontSize: 14,
                  fontFamily: "Roboto, Arial, sans-serif",
                }}
                borderRadius={6}
                px={4}
                h={10}
              >
                Redeem Points
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Box>
    </Center>
  );
};

const ImportExportTT = ({ toast, user }) => {
  const [currentPoints, setCurrentPoints] = useState("0.00");
  const [wantImportExport, setWantImportExport] = useState(null);
  const [exportAmount, setExportAmount] = useState("");
  const [importAmount, setImportAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);

  const fetchUserPoints = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const res = unwrap(
        await configurationApi.getUserLoyaltyPoints(user.token),
      );
      const pointsAvailable = res?.status
        ? res?.data?.loyaltyPoints?.points_available
        : null;
      setCurrentPoints(formatPointsLike(pointsAvailable, "0.00"));
    } catch {
      setCurrentPoints("0.00");
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    fetchUserPoints();
  }, [fetchUserPoints]);

  const handleConfirmExport = async () => {
    const pointsToExport = safeNum(exportAmount, NaN);
    const available = safeNum(currentPoints, 0);
    if (!Number.isFinite(pointsToExport) || pointsToExport <= 0) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please enter a valid amount to export"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    if (pointsToExport > available) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={`Insufficient points. You have ${formatPointsLike(available)} points available.`}
          />
        ),
        placement: "top",
        duration: 4000,
      });
      return;
    }
    setWorking(true);
    try {
      const res = unwrap(
        await configurationApi.exportFromTT(
          user.token,
          pointsToExport,
          "TT - Points",
          "Export from Business to TT - Points",
        ),
      );
      if (!res.status) throw new Error(res.status_msg || "Export failed");
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message={`${formatPointsLike(pointsToExport)} points exported successfully!`}
          />
        ),
        placement: "top",
        duration: 3000,
      });
      setExportAmount("");
      await fetchUserPoints();
    } catch (error) {
      const apiMsg =
        error?.response?.data?.status_msg ||
        error?.message ||
        "Failed to export points.";
      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={apiMsg} />
        ),
        placement: "top",
        duration: 5000,
      });
    } finally {
      setWorking(false);
    }
  };

  const handleConfirmImport = async () => {
    const pointsToImport = safeNum(importAmount, NaN);
    if (!Number.isFinite(pointsToImport) || pointsToImport <= 0) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please enter a valid amount to import"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    setWorking(true);
    try {
      const res = unwrap(
        await configurationApi.importToTT(
          user.token,
          pointsToImport,
          null,
          "Import to TT - Points",
        ),
      );
      if (!res.status) throw new Error(res.status_msg || "Import failed");
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message="Imported to TT successfully!"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      setImportAmount("");
      await fetchUserPoints();
    } catch (error) {
      const apiMsg =
        error?.response?.data?.status_msg ||
        error?.message ||
        "Failed to import.";
      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.red} message={apiMsg} />
        ),
        placement: "top",
        duration: 5000,
      });
    } finally {
      setWorking(false);
    }
  };

  return (
    <Center flex={1} style={styles.pageBg}>
      <Box w="100%" maxW="1200px" mx="auto" px={4}>
        <Box alignSelf="center" mb={4}>
          <Text
            style={{
              fontFamily: "Roboto, Arial, sans-serif",
              fontWeight: "400",
              fontSize: 18,
              color: GlobalStyles.colors.black,
            }}
          >
            Import / Export – Business User
          </Text>
        </Box>
        <Box
          width="100%"
          maxW={530}
          alignSelf="center"
          borderRadius="4px"
          borderWidth={1}
          borderColor="#BDBDBD"
          overflow="hidden"
        >
          <Box
            bg="#D9D9D9"
            px={5}
            py={3}
            borderBottomWidth={0.2}
            borderBottomColor="#000000"
          >
            <HStack alignItems="center" justifyContent="space-between">
              <Text
                style={{
                  fontFamily: "Roboto, Arial, sans-serif",
                  fontWeight: "400",
                  fontSize: 13,
                  color: GlobalStyles.colors.black,
                }}
              >
                Your Current TownTicks Points:
              </Text>
              <Box
                px={4}
                py={1.5}
                borderRadius={2}
                minW="70px"
                alignItems="center"
                borderWidth={2}
                borderColor={GlobalStyles.colors.blue}
                style={gradientStyle}
              >
                {loading ? (
                  <Spinner size="sm" color={GlobalStyles.colors.white} />
                ) : (
                  <Text
                    style={{
                      fontFamily: "Roboto, Arial, sans-serif",
                      fontWeight: "bold",
                      fontSize: 16,
                      color: GlobalStyles.colors.white,
                    }}
                  >
                    {currentPoints}
                  </Text>
                )}
              </Box>
            </HStack>
          </Box>
          <Box bg="#D9D9D940" px={5}>
            <HStack
              alignItems="center"
              justifyContent="space-between"
              py={3}
            >
              <Text
                style={{
                  fontFamily: "Roboto, Arial, sans-serif",
                  fontWeight: "400",
                  fontSize: 13,
                  color: GlobalStyles.colors.black,
                }}
              >
                You want to import or export?
              </Text>
              <HStack space={2}>
                <Pressable
                  onPress={() => setWantImportExport("export")}
                >
                  <Box
                    px={4}
                    py={1.5}
                    borderRadius={2}
                    borderWidth={2}
                    borderColor={GlobalStyles.colors.blue}
                    minW="70px"
                    alignItems="center"
                    style={
                      wantImportExport === "export"
                        ? gradientStyle
                        : { backgroundColor: GlobalStyles.colors.white }
                    }
                  >
                    <Text
                      style={{
                        fontFamily: "Roboto, Arial, sans-serif",
                        fontWeight: "bold",
                        fontSize: 13,
                        color:
                          wantImportExport === "export"
                            ? GlobalStyles.colors.white
                            : GlobalStyles.colors.blue,
                      }}
                    >
                      Export
                    </Text>
                  </Box>
                </Pressable>
                <Pressable
                  onPress={() => setWantImportExport("import")}
                >
                  <Box
                    px={4}
                    py={1.5}
                    borderRadius={2}
                    borderWidth={2}
                    borderColor={GlobalStyles.colors.blue}
                    minW="70px"
                    alignItems="center"
                    style={
                      wantImportExport === "import"
                        ? gradientStyle
                        : { backgroundColor: GlobalStyles.colors.white }
                    }
                  >
                    <Text
                      style={{
                        fontFamily: "Roboto, Arial, sans-serif",
                        fontWeight: "bold",
                        fontSize: 13,
                        color:
                          wantImportExport === "import"
                            ? GlobalStyles.colors.white
                            : GlobalStyles.colors.blue,
                      }}
                    >
                      Import
                    </Text>
                  </Box>
                </Pressable>
              </HStack>
            </HStack>

            {wantImportExport === "export" && (
              <>
                <HStack
                  alignItems="center"
                  justifyContent="space-between"
                  py={3}
                >
                  <Text
                    style={{
                      fontFamily: "Roboto, Arial, sans-serif",
                      fontWeight: "400",
                      fontSize: 13,
                      color: GlobalStyles.colors.black,
                    }}
                  >
                    How much TT points to export?
                  </Text>
                  <Box
                    px={4}
                    py={1.5}
                    borderRadius={2}
                    minW="70px"
                    alignItems="center"
                    borderWidth={2}
                    borderColor={GlobalStyles.colors.blue}
                    style={gradientStyle}
                  >
                    <Input
                      value={exportAmount}
                      onChangeText={setExportAmount}
                      variant="unstyled"
                      textAlign="center"
                      fontSize={16}
                      fontWeight="bold"
                      color={GlobalStyles.colors.white}
                      keyboardType="numeric"
                      placeholder="5"
                      placeholderTextColor="rgba(255,255,255,0.7)"
                      style={{
                        fontFamily: "Roboto, Arial, sans-serif",
                        width: "100%",
                      }}
                      _focus={{ bg: "transparent" }}
                      p={0}
                      m={0}
                    />
                  </Box>
                </HStack>
                <HStack
                  alignItems="center"
                  justifyContent="space-between"
                  py={3}
                >
                  <Text
                    style={{
                      fontFamily: "Roboto, Arial, sans-serif",
                      fontWeight: "400",
                      fontSize: 13,
                      color: GlobalStyles.colors.black,
                    }}
                  >
                    Export points from "Business" to TT - Points
                  </Text>
                  <Pressable
                    onPress={handleConfirmExport}
                    disabled={working || !exportAmount || loading}
                  >
                    <Box
                      px={5}
                      py={1.5}
                      borderRadius={2}
                      minW="85px"
                      alignItems="center"
                      style={gradientStyle}
                      opacity={
                        working || !exportAmount || loading ? 0.5 : 1
                      }
                    >
                      <Text
                        style={{
                          fontFamily: "Roboto, Arial, sans-serif",
                          fontWeight: "bold",
                          fontSize: 13,
                          color: GlobalStyles.colors.white,
                        }}
                      >
                        {working ? "..." : "Confirm"}
                      </Text>
                    </Box>
                  </Pressable>
                </HStack>
              </>
            )}

            {wantImportExport === "import" && (
              <>
                <HStack
                  alignItems="center"
                  justifyContent="space-between"
                  py={3}
                >
                  <Text
                    style={{
                      fontFamily: "Roboto, Arial, sans-serif",
                      fontWeight: "400",
                      fontSize: 13,
                      color: GlobalStyles.colors.black,
                    }}
                  >
                    How much TT points to import?
                  </Text>
                  <Box
                    px={4}
                    py={1.5}
                    borderRadius={2}
                    minW="70px"
                    alignItems="center"
                    borderWidth={2}
                    borderColor={GlobalStyles.colors.blue}
                    style={gradientStyle}
                  >
                    <Input
                      value={importAmount}
                      onChangeText={setImportAmount}
                      variant="unstyled"
                      textAlign="center"
                      fontSize={16}
                      fontWeight="bold"
                      color={GlobalStyles.colors.white}
                      keyboardType="numeric"
                      placeholder="5"
                      placeholderTextColor="rgba(255,255,255,0.7)"
                      style={{
                        fontFamily: "Roboto, Arial, sans-serif",
                        width: "100%",
                      }}
                      _focus={{ bg: "transparent" }}
                      p={0}
                      m={0}
                    />
                  </Box>
                </HStack>
                <HStack
                  alignItems="center"
                  justifyContent="space-between"
                  py={4}
                >
                  <Text
                    style={{
                      fontFamily: "Roboto, Arial, sans-serif",
                      fontWeight: "400",
                      fontSize: 13,
                      color: GlobalStyles.colors.black,
                    }}
                  >
                    Import points to TT
                  </Text>
                  <Pressable
                    onPress={handleConfirmImport}
                    disabled={working || loading || !importAmount}
                  >
                    <Box
                      px={5}
                      py={1.5}
                      borderRadius={2}
                      minW="85px"
                      alignItems="center"
                      style={gradientStyle}
                      opacity={
                        working || loading || !importAmount ? 0.5 : 1
                      }
                    >
                      <Text
                        style={{
                          fontFamily: "Roboto, Arial, sans-serif",
                          fontWeight: "bold",
                          fontSize: 13,
                          color: GlobalStyles.colors.white,
                        }}
                      >
                        {working ? "..." : "Confirm"}
                      </Text>
                    </Box>
                  </Pressable>
                </HStack>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Center>
  );
};

const LoyaltyManager = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState("settings");
  const toast = useToast();
  const user = useSelector((state) => state.user.user);
  const [settingsBtnTextColor, setSettingsBtnTextColor] = useState(
    GlobalStyles.colors.mustard,
  );
  const [analyticsBtnTextColor, setAnalyticsBtnTextColor] = useState(
    GlobalStyles.colors.blue,
  );
  const [redeemBtnTextColor, setRedeemBtnTextColor] = useState(
    GlobalStyles.colors.blue,
  );
  const [giftBtnTextColor, setGiftBtnTextColor] = useState(
    GlobalStyles.colors.blue,
  );

  function changeTabColor(tabType) {
    setSettingsBtnTextColor(
      tabType === "settings"
        ? GlobalStyles.colors.mustard
        : GlobalStyles.colors.blue,
    );
    setAnalyticsBtnTextColor(
      tabType === "analytics"
        ? GlobalStyles.colors.mustard
        : GlobalStyles.colors.blue,
    );
    setRedeemBtnTextColor(
      tabType === "redeem"
        ? GlobalStyles.colors.mustard
        : GlobalStyles.colors.blue,
    );
    setGiftBtnTextColor(
      tabType === "gift"
        ? GlobalStyles.colors.mustard
        : GlobalStyles.colors.blue,
    );
  }

  useEffect(() => {
    changeTabColor("settings");
    if (user?.token)
      axios.defaults.headers.common.Authorization = `Bearer ${user.token}`;
  }, [user]);

  return (
    <Box
      w="100%"
      bg={GlobalStyles.colors.lightGrey}
      minH={Platform.OS === "web" ? "100vh" : "100%"}
    >
      <Center>
        <Box w={["92%", 600, 860]} maxW="960px">
          {onBack && (
            <HStack w="100%" mb={3} px={4} mt={4}>
              <Pressable onPress={onBack}>
                <HStack alignItems="center" space={2}>
                  <Text
                    color={GlobalStyles.colors.blue}
                    fontWeight="500"
                    fontSize={14}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    ← Back to Services
                  </Text>
                </HStack>
              </Pressable>
            </HStack>
          )}
          <HStack
            space={2}
            mb={6}
            justifyContent="center"
            flexWrap="wrap"
          >
            <Button
              size="sm"
              mx={1}
              my={1}
              variant="solid"
              px={4}
              bg={GlobalStyles.colors.white}
              borderColor={settingsBtnTextColor}
              borderWidth={2}
              borderRadius={40}
              _hover={{ bg: GlobalStyles.colors.grey }}
              onPress={() => {
                setActiveTab("settings");
                changeTabColor("settings");
              }}
            >
              <Text
                fontWeight="600"
                fontFamily={GlobalStyles.text.fontFamily}
                color={settingsBtnTextColor}
              >
                Settings
              </Text>
            </Button>
            <Button
              size="sm"
              mx={1}
              my={1}
              variant="solid"
              px={4}
              bg={GlobalStyles.colors.white}
              borderColor={analyticsBtnTextColor}
              borderWidth={2}
              borderRadius={40}
              _hover={{ bg: GlobalStyles.colors.grey }}
              onPress={() => {
                setActiveTab("analytics");
                changeTabColor("analytics");
              }}
            >
              <Text
                fontWeight="600"
                fontFamily={GlobalStyles.text.fontFamily}
                color={analyticsBtnTextColor}
              >
                Analytics
              </Text>
            </Button>
            <Button
              size="sm"
              mx={1}
              my={1}
              variant="solid"
              px={4}
              bg={GlobalStyles.colors.white}
              borderColor={redeemBtnTextColor}
              borderWidth={2}
              borderRadius={40}
              _hover={{ bg: GlobalStyles.colors.grey }}
              onPress={() => {
                setActiveTab("redeem");
                changeTabColor("redeem");
              }}
            >
              <Text
                fontWeight="600"
                fontFamily={GlobalStyles.text.fontFamily}
                color={redeemBtnTextColor}
              >
                Redeem Points
              </Text>
            </Button>
            <Button
              size="sm"
              mx={1}
              my={1}
              variant="solid"
              px={4}
              bg={GlobalStyles.colors.white}
              borderColor={giftBtnTextColor}
              borderWidth={2}
              borderRadius={40}
              _hover={{ bg: GlobalStyles.colors.grey }}
              onPress={() => {
                setActiveTab("gift");
                changeTabColor("gift");
              }}
            >
              <Text
                fontWeight="600"
                fontFamily={GlobalStyles.text.fontFamily}
                color={giftBtnTextColor}
              >
                Import/Export
              </Text>
            </Button>
          </HStack>
        </Box>
      </Center>
      <Box pb="48px">
        {activeTab === "settings" && (
          <LoyaltySettings toast={toast} user={user} />
        )}
        {activeTab === "analytics" && (
          <LoyaltyAnalytics toast={toast} user={user} />
        )}
        {activeTab === "redeem" && (
          <RedeemPoints toast={toast} user={user} />
        )}
        {activeTab === "gift" && (
          <ImportExportTT toast={toast} user={user} />
        )}
      </Box>
    </Box>
  );
};

export default LoyaltyManager;